import { centerSlot, slotOffsetM, type Formation } from './formations';

export interface SwarmDrone {
  robotId: string;
  lat: number;
  lon: number;
  alt_m: number;
}

export interface SwarmWaypoint {
  lat: number;
  lon: number;
  alt_m: number;
}

export interface PhaseTargets {
  /** Per-drone phase-1 waypoint (concentric circles oriented along target vector). */
  phase1: Record<string, SwarmWaypoint>;
  /** Per-drone phase-2 waypoint (rigid translation to target). */
  phase2: Record<string, SwarmWaypoint>;
}

const EARTH_R = 6_371_000;

function project(lat0: number, lon0: number, lat: number, lon: number): [number, number] {
  // equirectangular projection — east_m, north_m
  const dLatRad = ((lat - lat0) * Math.PI) / 180;
  const dLonRad = ((lon - lon0) * Math.PI) / 180;
  const cosLat0 = Math.cos((lat0 * Math.PI) / 180);
  return [dLonRad * cosLat0 * EARTH_R, dLatRad * EARTH_R];
}

function unproject(lat0: number, lon0: number, eastM: number, northM: number): [number, number] {
  const cosLat0 = Math.cos((lat0 * Math.PI) / 180);
  const dLat = ((northM / EARTH_R) * 180) / Math.PI;
  const dLon = ((eastM / (EARTH_R * cosLat0)) * 180) / Math.PI;
  return [lat0 + dLat, lon0 + dLon];
}

export function computePhaseTargets(
  drones: SwarmDrone[],
  target: SwarmWaypoint,
  formation: Formation,
  spacingM = 8,
  altitudeStaggerM = 2,
): PhaseTargets {
  if (drones.length === 0) {
    return { phase1: {}, phase2: {} };
  }
  const n = drones.length;
  const ci = centerSlot(formation, n);
  const centerDrone = drones[ci]!;

  const [fwdE, fwdN] = project(centerDrone.lat, centerDrone.lon, target.lat, target.lon);
  const heading = Math.atan2(fwdE, fwdN);
  const cosH = Math.cos(heading);
  const sinH = Math.sin(heading);

  const phase1: Record<string, SwarmWaypoint> = {};
  const phase2: Record<string, SwarmWaypoint> = {};

  drones.forEach((drone, i) => {
    const [dx, dy] = slotOffsetM(formation, i, n, spacingM);
    const east = dx * cosH + dy * sinH;
    const north = -dx * sinH + dy * cosH;
    const alt = target.alt_m + i * altitudeStaggerM;

    const [p1Lat, p1Lon] = unproject(centerDrone.lat, centerDrone.lon, east, north);
    phase1[drone.robotId] = { lat: p1Lat, lon: p1Lon, alt_m: alt };

    const [p2Lat, p2Lon] = unproject(target.lat, target.lon, east, north);
    phase2[drone.robotId] = { lat: p2Lat, lon: p2Lon, alt_m: alt };
  });

  return { phase1, phase2 };
}
